import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Divider } from 'antd';
import { MacCommandOutlined, PlusCircleFilled } from '@ant-design/icons';
import { Catcher, LoadingApiByIds } from '../';
import cx from 'classnames';
import { IMeta } from '../../REST';
import { ITariff, ITariffsList } from '../../interfaces';
import { useProfile, useProfileIsRight, useRouterStore, useUiIsSpinnerFound } from '../../hooks';
import { getCompaniesChildrenSelect } from '../../middleware';
import { getTariffPackagesList, getTariffsList } from '../../middleware';
import { tNM, tP, tT } from '../../translate';
import { TariffCard, TariffCreationModal } from './Components';
import { InfiniteScroll } from './Components/InfiniteScroll';
import Styles from '../styles/m_itemCardStyles.less';
import TariffsStyles from './m_styles.less';

export const TariffsPage: React.FC = observer(() => {
    const [isOpen, setIsOpen] = useState(false);
    const companyId = useProfile().companyId;
    const routerStore = useRouterStore();
    const [page, setPage] = useState(1);
    const [tariffs, setTariffs] = useState<ITariff[]>([]);
    const [total, setTotal] = useState(0);
    const pageSize = 10;
    const isLoading = useUiIsSpinnerFound(['GET_TARIFFS']);
    const isUser = useProfileIsRight('USER');

    const openStepper = () => {
        setIsOpen(true);
    };

    const openEditStepper = () => {
        setIsOpen(true);
    };

    const closeStepper = () => {
        setIsOpen(false);
    };

    const goTo = (event: React.MouseEvent<HTMLDivElement>, id: string | undefined): void => {
        if (!(event.ctrlKey || event.metaKey || event.altKey || event.shiftKey)) {
            event.preventDefault();

            routerStore.goTo('TariffItem', { params: { id } }).then();
        }
    };

    const fetchData = async (page: number, reset?: boolean) => {
        const resp: { list: ITariffsList; meta: IMeta } = (await getTariffsList(
            { pageNumber: page, pageSize: pageSize },
            true,
        )) as { list: ITariffsList; meta: IMeta };

        const { list, meta } = resp;
        const { records_count } = meta;

        if (reset) {
            setTariffs(list);
        } else {
            setTariffs((prevList) => [...prevList, ...list]);
        }

        setTotal(records_count || 0);
    };

    useEffect(() => {
        fetchData(page).then();
        getTariffPackagesList().then();

        if (companyId) {
            getCompaniesChildrenSelect(companyId || '', true).then();
        }
    }, []);

    return (
        <Catcher>
            <div className={cx(Styles.main, TariffsStyles.container)}>
                <div className={TariffsStyles.tariffItemContainer}>
                    <div className={TariffsStyles.tariffItemHeaderContainer}>
                        <div>
                            <div className={TariffsStyles.header}>
                                <MacCommandOutlined className={TariffsStyles.icon} />
                                {tP('Tariffs')}
                            </div>
                            <p>{tT('Manage tariff plans and package configurations')}</p>
                        </div>
                        {isUser ? (
                            <Button type={'primary'} onClick={openStepper}>
                                <PlusCircleFilled /> {tP('Create New Tariff')}
                            </Button>
                        ) : null}
                    </div>
                    <Divider />
                    {tariffs.length === 0 && !isLoading && (
                        <div className={TariffsStyles.notFound}>{tNM('No tariffs found')}</div>
                    )}
                    <InfiniteScroll
                        pageSize={pageSize}
                        page={page}
                        fetchFunc={fetchData}
                        totalPackage={total}
                        currentPackages={tariffs.length}
                        handleSetPage={setPage}
                    >
                        <div className={TariffsStyles.infiniteScrollTariffsPageContainer}>
                            {tariffs.map((item) => (
                                <TariffCard
                                    {...item}
                                    onEdit={openEditStepper}
                                    onClick={(e) => goTo(e, item.id)}
                                    key={item.id}
                                    cardsCount={item.cardsCount}
                                />
                            ))}
                        </div>
                    </InfiniteScroll>
                    <TariffCreationModal
                        closeStepper={closeStepper}
                        isOpen={isOpen}
                        fetchData={() => fetchData(1, true)}
                    />
                </div>
                <LoadingApiByIds idSpinners={['GET_TARIFFS']} />
            </div>
        </Catcher>
    );
});
